import {
  collection, getCountFromServer, getDocs, orderBy, query, Timestamp, updateDoc, where, limit, startAfter,
} from 'firebase/firestore';

import { db } from '@/config/firebase';

class UserService {
  static async getUsers() {
    const querySnapshot = await getDocs(collection(db, 'users'));
    const users: any = [];
    let index = 1;

    querySnapshot.forEach((doc: any) => {
      // eslint-disable-next-line no-plusplus
      users.push({ id: index++, ...doc.data() });
    });
    return users;
  }

  static async getUserPushTokens() {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      // where('email', 'in', ['sam1@gmail.com', 'contact@talabapp.co.uk']), // test
      // where('platform', '==', 'ios'),
      orderBy('pushToken'),
    );

    const querySnapshot = await getDocs(q);
    const pushTokens: Record<string, string>[] = [];

    querySnapshot.forEach((doc: any) => {
      const data = doc.data();
      if (data.pushToken) {
        pushTokens.push({
          email: data.email,
          token: data.pushToken,
        });
      }
    });

    return pushTokens;
  }

  static async getUserByUid(uid: string) {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('userId', '==', uid));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      console.log(querySnapshot.docs[0].data());
      return { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() };
    }
    return null;
  }

  static async getUsersReferredBy(partnerId: string) {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('referredBy', '==', partnerId));
    const querySnapshot = await getDocs(q);
    const users: any[] = [];
    querySnapshot.forEach((doc: any) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    return users;
  }

  static async getUsersCount() {
    const countSnap = await getCountFromServer(collection(db, 'users'));
    return countSnap.data().count;
  }

  /**
   * Fetches a page of users, ordered by registrationDate desc.
   * @param {number} pageSize - Number of users per page
   * @param {any} lastDoc - The last document from the previous page (for cursor-based pagination)
   * @returns {Promise<{users: any[], lastDoc: any}>}
   */
  static async getUsersPaginated(pageSize = 10, lastDoc = null) {
    let q = query(collection(db, 'users'), orderBy('registrationDate', 'desc'), limit(pageSize));
    if (lastDoc) {
      q = query(collection(db, 'users'), orderBy('registrationDate', 'desc'), startAfter(lastDoc), limit(pageSize));
    }
    const querySnapshot = await getDocs(q);
    const users: any[] = [];
    querySnapshot.forEach((doc: any) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
    return { users, lastDoc: lastVisible };
  }

  /**
   * Fetches the referral code for a partner by userId from the referrals collection.
   * @param {string} userId
   * @returns {Promise<string|null>} referralCode or null
   */
  static async getReferralCodeByUserId(userId: string): Promise<string | null> {
    const referralsRef = collection(db, 'referrals');
    const q = query(referralsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const data = querySnapshot.docs[0].data();
      return data.referralCode || null;
    }
    return null;
  }

  /**
   * Returns all users with role === 'partner'.
   */
  static async getPartners() {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('role', '==', 'partner'));
    const querySnapshot = await getDocs(q);
    const partners: any[] = [];
    querySnapshot.forEach((doc: any) => {
      partners.push({ id: doc.id, ...doc.data() });
    });
    return partners;
  }

  /**
   * Returns users who have "team-member" in roles array.
   */
  static async getTeamMembers() {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('roles', 'array-contains', 'team-member'));
    const querySnapshot = await getDocs(q);
    const members: any[] = [];
    querySnapshot.forEach((doc: any) => {
      members.push({ id: doc.id, ...doc.data() });
    });
    return members;
  }

  /**
   * Returns a map of userId -> referralCode for all entries in the referrals collection.
   */
  static async getAllReferralsMap(): Promise<Record<string, string>> {
    const referralsRef = collection(db, 'referrals');
    const querySnapshot = await getDocs(referralsRef);
    const map: Record<string, string> = {};
    querySnapshot.forEach((doc: any) => {
      const data = doc.data();
      if (data.userId && data.referralCode) {
        map[data.userId] = data.referralCode;
      }
    });
    return map;
  }
}

export default UserService;
